import type { ReactNode } from "react";
import { ErrorOverlay } from "./ErrorOverlay";
import { NoRowsOverlay } from "./NoRowsOverlay";

export interface TableOverlayState {
  error: string | null;
  isFetching: boolean;
  totalCount: number;
  isInitialLoading: boolean;
}

export interface NoRowsOverlayActions {
  isAppliedFilters: boolean;
  onReset?: () => void;
}

export interface CreateTableOverlayConfig {
  /** Hook that returns current table state (error, loading, count). */
  useTableState: () => TableOverlayState;
  /** Message when no rows and filters are applied. */
  noRowsMessageWithFilters: string;
  /** Message when no rows and no filters applied. */
  noRowsMessageWithoutFilters: string;
  /** Hook that returns no-rows actions (e.g. has filters, reset handler). */
  useNoRowsActions: () => NoRowsOverlayActions;
  /** Title shown in error overlay (e.g. "Failed to Load Articles"). */
  errorTitle: string;
  /** Hook that returns a function to clear error (called before optional onRetry). */
  useClearError: () => () => void;
}

/**
 * Creates a unified overlay component that shows error, no-rows, or nothing
 * based on config (store state + messages). Reused by articles and narrative clusters tables.
 */
export const createTableOverlay = (
  config: CreateTableOverlayConfig,
  onRetry?: () => void,
): (() => ReactNode) => {
  const UnifiedOverlay = () => {
    const state = config.useTableState();
    const noRowsActions = config.useNoRowsActions();
    const clearError = config.useClearError();

    const shouldShowOverlay =
      !state.isFetching &&
      !state.error &&
      !state.isInitialLoading &&
      state.totalCount === 0;
    const shouldShowErrorOverlay = !state.isFetching && !!state.error;

    const handleRetry = () => {
      clearError();
      onRetry?.();
    };

    if (shouldShowErrorOverlay) {
      return <ErrorOverlay title={config.errorTitle} onRetry={handleRetry} />;
    }
    if (shouldShowOverlay) {
      const message = noRowsActions.isAppliedFilters
        ? config.noRowsMessageWithFilters
        : config.noRowsMessageWithoutFilters;
      return (
        <NoRowsOverlay
          message={message}
          showResetButton={noRowsActions.isAppliedFilters && !!noRowsActions.onReset}
          onReset={noRowsActions.onReset}
        />
      );
    }
    return null;
  };

  return UnifiedOverlay;
};
